import React, {useState} from 'react';
import {useSortable} from '@dnd-kit/sortable';
import {CSS} from '@dnd-kit/utilities';
import {
    Box,
    Paper,
    Typography,
    TextField,
    Select,
    MenuItem,
    Checkbox,
    FormControlLabel,
    IconButton,
    Button,
    Chip,
    FormControl,
    InputLabel,
    FormGroup,
    FormLabel,
    RadioGroup,
    Radio,
    Collapse,
    Divider,
    useTheme,
} from '@mui/material';
import {
    DragHandle as DragHandleIcon,
    Delete as DeleteIcon,
    Add as AddIcon,
    Settings as SettingsIcon,
    ExpandMore as ExpandMoreIcon,
    ExpandLess as ExpandLessIcon,
    CheckCircle as CheckCircleIcon
} from '@mui/icons-material';

export const Question = ({
                             id,
                             sectionId,
                             question,
                             onUpdate,
                             onDelete,
                             isOverlay = false,
                             editable = true,
                             availableSections = [],
                             onMoveToSection
                         }) => {
    const theme = useTheme(); // Add theme hook
    const {
        attributes,
        listeners,
        setNodeRef,
        transform,
        transition,
        isDragging
    } = useSortable({
        id,
        data: {
            sectionId
        },
        disabled: !editable // Disable drag when not editable
    });

    const [isAdvancedOpen, setIsAdvancedOpen] = useState(false);
    const [configText, setConfigText] = useState(JSON.stringify(question.config || {}, null, 2));
    const [configError, setConfigError] = useState(null);
    const [selectedTargetSection, setSelectedTargetSection] = useState(sectionId);

    const style = {
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isDragging ? 0.5 : 1,
        zIndex: isOverlay ? 1000 : 'auto'
    };

    // Helper function to check if an option is correct
    const isOptionCorrect = (optionValue) => {
        if (!question.config?.correct_answer) return false;

        const correctAnswer = question.config.correct_answer;

        // Handle both string (single choice) and array (multiple choice) formats
        if (Array.isArray(correctAnswer)) {
            return correctAnswer.includes(optionValue);
        } else {
            return correctAnswer === optionValue;
        }
    };

    const handleTextChange = (e) => {
        onUpdate(sectionId, question.id, {text: e.target.value});
    };

    const handleTypeChange = (e) => {
        const newType = e.target.value;
        let updates = {type: newType};

        if (['single_choice', 'multi_choice'].includes(newType) && !question.options?.length) {
            updates.options = [
                {id: crypto.randomUUID(), label: 'Option 1', value: 'option1', order: 0}
            ];
        }

        onUpdate(sectionId, question.id, updates);
    };

    const handleRequiredChange = (e) => {
        onUpdate(sectionId, question.id, {required: e.target.checked});
    };

    const handleHiddenChange = (e) => {
        onUpdate(sectionId, question.id, {hidden: e.target.checked});
    };

    const handleDisabledChange = (e) => {
        onUpdate(sectionId, question.id, {disabled: e.target.checked});
    };

    const handleDelete = () => {
        onDelete(sectionId, question.id);
    };

    const toggleAdvanced = () => {
        setIsAdvancedOpen(!isAdvancedOpen);
    };

    const handleConfigChange = (e) => {
        setConfigText(e.target.value);
        try {
            const configObj = JSON.parse(e.target.value);
            setConfigError(null);
        } catch (error) {
            setConfigError("Invalid JSON format");
        }
    };

    const saveConfig = () => {
        try {
            const configObj = JSON.parse(configText);
            onUpdate(sectionId, question.id, {config: configObj});
            setConfigError(null);
        } catch (error) {
            setConfigError("Failed to save: Invalid JSON format");
        }
    };

    const addOption = () => {
        const newOption = {
            id: crypto.randomUUID(),
            label: `Option ${question.options.length + 1}`,
            value: `option${question.options.length + 1}`,
            order: question.options.length
        };

        onUpdate(sectionId, question.id, {
            options: [...question.options, newOption]
        });
    };

    const updateOption = (optionId, updates) => {
        onUpdate(sectionId, question.id, {
            options: question.options.map(option =>
                option.id === optionId ? {...option, ...updates} : option
            )
        });
    };

    // Handle updating correct answer for text/number questions
    const handleCorrectAnswerChange = (newAnswer) => {
        const currentConfig = question.config || {};
        const newConfig = {
            ...currentConfig,
            correct_answer: newAnswer || null
        };

        // Remove correct_answer if it's empty
        if (!newAnswer) {
            delete newConfig.correct_answer;
        }

        onUpdate(sectionId, question.id, {config: newConfig});

        // Also update the configText state to reflect changes
        setConfigText(JSON.stringify(newConfig, null, 2));
    };
    const toggleCorrectAnswer = (optionValue) => {
        const currentConfig = question.config || {};
        let newCorrectAnswer;

        if (question.type === 'single_choice') {
            // For single choice, set this as the only correct answer or clear if already correct
            newCorrectAnswer = isOptionCorrect(optionValue) ? null : optionValue;
        } else if (question.type === 'multi_choice') {
            // For multiple choice, toggle this option in the array
            const currentAnswers = Array.isArray(currentConfig.correct_answer)
                ? currentConfig.correct_answer
                : (currentConfig.correct_answer ? [currentConfig.correct_answer] : []);

            if (currentAnswers.includes(optionValue)) {
                // Remove from correct answers
                newCorrectAnswer = currentAnswers.filter(val => val !== optionValue);
                if (newCorrectAnswer.length === 0) newCorrectAnswer = null;
            } else {
                // Add to correct answers
                newCorrectAnswer = [...currentAnswers, optionValue];
            }
        }

        // Update the config with new correct_answer
        const newConfig = {
            ...currentConfig,
            correct_answer: newCorrectAnswer
        };

        // Remove correct_answer if it's null/empty
        if (newCorrectAnswer === null) {
            delete newConfig.correct_answer;
        }

        onUpdate(sectionId, question.id, {config: newConfig});

        // Also update the configText state to reflect changes
        setConfigText(JSON.stringify(newConfig, null, 2));
    };

    const deleteOption = (optionId) => {
        onUpdate(sectionId, question.id, {
            options: question.options
                .filter(option => option.id !== optionId)
                .map((option, index) => ({...option, order: index}))
        });
    };

    const handleMoveToSection = () => {
        if (selectedTargetSection && selectedTargetSection !== sectionId && onMoveToSection) {
            onMoveToSection(sectionId, question.id, selectedTargetSection);
        }
    };

    // Render the question in read-only mode
    const renderReadOnlyQuestion = () => {
        return (
            <Box>
                <Box mb={2}>
                    <Typography variant="subtitle1" sx={{fontWeight: 'medium'}}>
                        {question.text}
                        {question.required && (
                            <Typography component="span" color="error" sx={{ml: 0.5}}>*</Typography>
                        )}
                    </Typography>

                    <Chip
                        label={getQuestionTypeLabel(question.type)}
                        size="small"
                        variant="outlined"
                        sx={{mt: 1, mr: 1}}
                    />

                    {question.hidden && (
                        <Chip
                            label="Hidden"
                            size="small"
                            color="default"
                            sx={{mt: 1, mr: 1}}
                        />
                    )}

                    {question.disabled && (
                        <Chip
                            label="Disabled"
                            size="small"
                            color="default"
                            sx={{mt: 1}}
                        />
                    )}
                </Box>

                {/* Render the appropriate input control based on question type */}
                {renderReadOnlyControl()}

                {/* Show explanation if available */}
                {question.config?.explanation && (
                    <Box sx={{
                        mt: 2,
                        p: 2,
                        borderRadius: 1
                    }}>
                        <Typography variant="body2">
                            <strong>Justification:</strong> {question.config.explanation}
                        </Typography>
                    </Box>
                )}
            </Box>
        );
    };

    // Get human-readable question type label
    const getQuestionTypeLabel = (type) => {
        const typeMap = {
            'text': 'Long Text',
            'string': 'Short Text',
            'number': 'Number',
            'single_choice': 'Single Choice',
            'multi_choice': 'Multiple Choice'
        };
        return typeMap[type] || type;
    };

    // Render the appropriate read-only control based on question type
    const renderReadOnlyControl = () => {
        switch (question.type) {
            case 'text':
                return (
                    <>
                        <TextField
                            fullWidth
                            multiline
                            rows={3}
                            placeholder="Answer would go here..."
                            disabled
                            variant="outlined"
                        />
                        {question.config?.correct_answer && (
                            <Box sx={{
                                mt: 2,
                                p: 2,
                                border: 1,
                                borderColor: 'success.main',
                                borderRadius: 1
                            }}>
                                <Typography variant="body2">
                                    <strong>Example Answer:</strong> {question.config.correct_answer}
                                </Typography>
                            </Box>
                        )}
                    </>
                );

            case 'string':
                return (
                    <>
                        <TextField
                            fullWidth
                            placeholder="Answer would go here..."
                            disabled
                            variant="outlined"
                        />
                        {question.config?.correct_answer && (
                            <Box sx={{
                                mt: 2,
                                p: 2,
                                border: 1,
                                borderColor: 'success.main',
                                borderRadius: 1
                            }}>
                                <Typography variant="body2">
                                    <strong>Example Answer:</strong> {question.config.correct_answer}
                                </Typography>
                            </Box>
                        )}
                    </>
                );

            case 'number':
                return (
                    <>
                        <TextField
                            fullWidth
                            type="number"
                            placeholder="0"
                            disabled
                            variant="outlined"
                        />
                        {question.config?.correct_answer && (
                            <Box sx={{
                                mt: 2,
                                p: 2,
                                border: 1,
                                borderColor: 'success.main',
                                borderRadius: 1
                            }}>
                                <Typography variant="body2">
                                    <strong>Correct Answer:</strong> {question.config.correct_answer}
                                </Typography>
                            </Box>
                        )}
                    </>
                );

            case 'single_choice':
                return (
                    <FormControl component="fieldset" disabled>
                        <RadioGroup>
                            {question.options?.map(option => {
                                const isCorrect = isOptionCorrect(option.value);
                                return (
                                    <Box
                                        key={option.id}
                                        sx={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            border: isCorrect ? 2 : 1,
                                            borderColor: isCorrect ? 'success.main' : 'divider',
                                            borderRadius: 1,
                                            p: 1,
                                            mb: 1
                                        }}
                                    >
                                        <FormControlLabel
                                            value={option.value}
                                            control={<Radio/>}
                                            label={option.label}
                                            sx={{
                                                color: isCorrect ? 'success.main' : 'inherit',
                                                fontWeight: isCorrect ? 'bold' : 'normal',
                                                flex: 1
                                            }}
                                        />
                                    </Box>
                                );
                            })}
                        </RadioGroup>
                    </FormControl>
                );

            case 'multi_choice':
                return (
                    <FormGroup>
                        {question.options?.map(option => {
                            const isCorrect = isOptionCorrect(option.value);
                            return (
                                <Box
                                    key={option.id}
                                    sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        border: isCorrect ? 2 : 1,
                                        borderColor: isCorrect ? 'success.main' : 'divider',
                                        borderRadius: 1,
                                        p: 1,
                                        mb: 1
                                    }}
                                >
                                    <FormControlLabel
                                        control={<Checkbox disabled/>}
                                        label={option.label}
                                        sx={{
                                            color: isCorrect ? 'success.main' : 'inherit',
                                            fontWeight: isCorrect ? 'bold' : 'normal',
                                            flex: 1
                                        }}
                                    />
                                </Box>
                            );
                        })}
                    </FormGroup>
                );

            default:
                return (
                    <Typography variant="body2" color="text.secondary">
                        Preview not available for this question type
                    </Typography>
                );
        }
    };

    // Render the editable question UI
    const renderEditableQuestion = () => {
        return (
            <>
                <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                    {/* Left side with drag handle and question type */}
                    <Box display="flex" alignItems="center" gap={1}>
                        <IconButton {...listeners} size="small">
                            <DragHandleIcon/>
                        </IconButton>

                        <Select
                            value={question.type}
                            onChange={handleTypeChange}
                            size="small"
                            sx={{minWidth: 180}}
                        >
                            <MenuItem value="text">Long Text</MenuItem>
                            <MenuItem value="string">Short Text</MenuItem>
                            <MenuItem value="number">Number</MenuItem>
                            <MenuItem value="single_choice">Single Choice</MenuItem>
                            <MenuItem value="multi_choice">Multiple Choice</MenuItem>
                        </Select>
                    </Box>

                    {/* Right side with advanced and delete buttons */}
                    <Box>
                        <IconButton
                            onClick={toggleAdvanced}
                            size="small"
                            color="primary"
                            title="Settings"
                        >
                            {isAdvancedOpen ? <ExpandLessIcon fontSize="small"/> : <SettingsIcon fontSize="small"/>}
                        </IconButton>

                        <IconButton
                            onClick={handleDelete}
                            size="small"
                            color="error"
                            title="Delete Question"
                        >
                            <DeleteIcon fontSize="small"/>
                        </IconButton>
                    </Box>
                </Box>

                <TextField
                    fullWidth
                    value={question.text}
                    onChange={handleTextChange}
                    placeholder="Question text"
                    variant="standard"
                    sx={{mb: 2}}
                />

                {/* Checkbox controls in a row */}
                <Box sx={{display: 'flex', flexWrap: 'wrap', mb: 2}}>
                    <FormControlLabel
                        control={
                            <Checkbox
                                checked={question.required || false}
                                onChange={handleRequiredChange}
                            />
                        }
                        label="Required"
                    />

                    <FormControlLabel
                        control={
                            <Checkbox
                                checked={question.hidden || false}
                                onChange={handleHiddenChange}
                            />
                        }
                        label="Hidden"
                    />

                    <FormControlLabel
                        control={
                            <Checkbox
                                checked={question.disabled || false}
                                onChange={handleDisabledChange}
                            />
                        }
                        label="Disabled"
                    />
                </Box>

                {/* Correct Answer section for text and number questions */}
                {(['text', 'string', 'number'].includes(question.type)) && (
                    <Box sx={{mt: 2, mb: 2}}>
                        <Typography variant="subtitle2" gutterBottom>
                            {question.type === 'number' ? 'Correct Answer' : 'Example Answer'}
                        </Typography>

                        <TextField
                            fullWidth
                            multiline={question.type === 'text'}
                            rows={question.type === 'text' ? 3 : 1}
                            type={question.type === 'number' ? 'number' : 'text'}
                            value={question.config?.correct_answer || ''}
                            onChange={(e) => handleCorrectAnswerChange(e.target.value)}
                            placeholder={
                                question.type === 'number'
                                    ? 'Enter the correct numerical answer'
                                    : 'Enter an example answer for this question'
                            }
                            variant="outlined"
                            size="small"
                        />
                    </Box>
                )}

                {/* Advanced configuration section - FIXED BACKGROUND */}
                <Collapse in={isAdvancedOpen}>
                    <Box sx={{
                        mt: 2,
                        mb: 2,
                        p: 2,
                        bgcolor: theme.palette.mode === 'dark' ? 'grey.800' : 'grey.100',
                        borderRadius: 1
                    }}>
                        <Typography variant="subtitle2" gutterBottom>
                            Advanced Configuration
                        </Typography>

                        <Typography variant="caption" color="textSecondary" display="block" gutterBottom>
                            Question ID: {question.id}
                        </Typography>

                        <Typography variant="caption" gutterBottom>
                            Configuration JSON:
                        </Typography>

                        <TextField
                            fullWidth
                            multiline
                            rows={4}
                            value={configText}
                            onChange={handleConfigChange}
                            variant="outlined"
                            size="small"
                            placeholder="{}"
                            error={!!configError}
                            helperText={configError}
                            sx={{
                                mb: 1,
                                fontFamily: 'monospace',
                                '& .MuiInputBase-input': {fontFamily: 'monospace'}
                            }}
                        />

                        <Button
                            variant="contained"
                            size="small"
                            onClick={saveConfig}
                            disabled={!!configError}
                        >
                            Apply Config
                        </Button>

                        {/* Section move controls - only show if we have sections and a callback */}
                        {availableSections.length > 1 && onMoveToSection && (
                            <>
                                <Divider sx={{my: 2}}/>
                                <Typography variant="subtitle2" gutterBottom>
                                    Move to Section
                                </Typography>
                                <Typography variant="caption" color="textSecondary" display="block" gutterBottom>
                                    Move this question to a different section
                                </Typography>
                                <Box sx={{display: 'flex', gap: 1, alignItems: 'center', mt: 1}}>
                                    <FormControl size="small" fullWidth>
                                        <Select
                                            value={selectedTargetSection}
                                            onChange={(e) => setSelectedTargetSection(e.target.value)}
                                        >
                                            {availableSections.map((section) => (
                                                <MenuItem
                                                    key={section.id}
                                                    value={section.id}
                                                    disabled={section.id === sectionId}
                                                >
                                                    {section.title} {section.id === sectionId ? '(current)' : ''}
                                                </MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>
                                    <Button
                                        variant="outlined"
                                        size="small"
                                        onClick={handleMoveToSection}
                                        disabled={selectedTargetSection === sectionId}
                                        sx={{whiteSpace: 'nowrap'}}
                                    >
                                        Move
                                    </Button>
                                </Box>
                            </>
                        )}
                    </Box>
                    <Divider/>
                </Collapse>

                {/* Choice options section */}
                {(question.type === 'single_choice' || question.type === 'multi_choice') && (
                    <Box sx={{mt: 2}}>
                        <Typography variant="subtitle2" gutterBottom>
                            Options
                        </Typography>

                        <Typography variant="caption" color="text.secondary" display="block" gutterBottom>
                            {question.type === 'single_choice'
                                ? 'Use the radio buttons to select the correct answer:'
                                : 'Use the checkboxes to select the correct answers:'
                            }
                        </Typography>

                        {question.options.map(option => {
                            const isCorrect = isOptionCorrect(option.value);
                            return (
                                <Box key={option.id} display="flex" gap={1} alignItems="center" mb={1}>
                                    {/* Toggle for correct answer */}
                                    {question.type === 'single_choice' ? (
                                        <Radio
                                            checked={isCorrect}
                                            onChange={() => toggleCorrectAnswer(option.value)}
                                            size="small"
                                            color="success"
                                            title="Mark as correct answer"
                                        />
                                    ) : (
                                        <Checkbox
                                            checked={isCorrect}
                                            onChange={() => toggleCorrectAnswer(option.value)}
                                            size="small"
                                            color="success"
                                            title="Mark as correct answer"
                                        />
                                    )}

                                    <TextField
                                        size="small"
                                        value={option.label}
                                        onChange={(e) => updateOption(option.id, {label: e.target.value})}
                                        placeholder="Option label"
                                        fullWidth
                                        sx={{
                                            '& .MuiOutlinedInput-root': {
                                                borderColor: isCorrect ? 'success.main' : undefined,
                                                '&:hover': {
                                                    borderColor: isCorrect ? 'success.main' : undefined,
                                                }
                                            }
                                        }}
                                    />

                                    <IconButton
                                        onClick={() => deleteOption(option.id)}
                                        size="small"
                                        color="error"
                                        title="Delete option"
                                    >
                                        <DeleteIcon fontSize="small"/>
                                    </IconButton>
                                </Box>
                            );
                        })}

                        <Button
                            onClick={addOption}
                            startIcon={<AddIcon/>}
                            size="small"
                        >
                            Add Option
                        </Button>
                    </Box>
                )}
            </>
        );
    };

    return (
        <Paper
            variant="outlined"
            ref={setNodeRef}
            style={style}
            elevation={isOverlay ? 8 : 2}
            sx={{
                mb: 2,
                p: 2,
                position: 'relative',
                backgroundColor: editable
                    ? 'inherit'
                    : theme.palette.mode === 'dark'
                        ? 'rgba(255, 255, 255, 0.05)'
                        : 'rgba(0, 0, 0, 0.03)',
            }}
            {...(editable ? attributes : {})}
        >
            {editable ? renderEditableQuestion() : renderReadOnlyQuestion()}
        </Paper>
    );
};